import { useCallback, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ThemeProvider } from '@xdocs/ui';
import type { ThemePreference } from '@xdocs/ui';

const THEME_STORAGE_KEY = 'xdocs.theme';

function isThemePreference(value: string): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

function readStoredTheme(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw && isThemePreference(raw)) return raw;
  } catch {
    // localStorage unavailable — fall back to 'light' (see default below).
  }
  return 'light';
}

export interface ThemeControllerProps {
  children: ReactNode;
}

/**
 * Owns `users.theme` persistence for `apps/web`. First-visit default is
 * `'light'` — mockup ground truth (`mockups-extracted/13-login`,
 * `18-documents`) always boots light with dark reachable only via the
 * explicit `<ThemeToggle>`/Appearance-settings switch; it never auto-follows
 * `prefers-color-scheme` on an unvisited browser. `'system'` remains a
 * selectable preference (Settings → Appearance keeps its System/Light/Dark
 * control, and `@xdocs/ui`'s `ThemeProvider` still resolves it against the
 * OS scheme) — this only changes what a browser with NOTHING stored yet
 * renders on the very first paint. Deliberately mounted ABOVE auth/WS in the
 * provider tree (`App.tsx`) — `/login`/`/signup` need a themed shell before
 * any session exists, so this never depends on the WS client.
 * `useSyncThemeToServer` (mounted only inside the authenticated shell)
 * best-effort mirrors a change to `settings.setTheme`; localStorage stays
 * the cross-reload source of truth for this browser regardless of auth state.
 */
export function ThemeController({ children }: ThemeControllerProps): ReactElement {
  const [theme, setThemeState] = useState<ThemePreference>(() => readStoredTheme());

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // best-effort persistence only — the in-memory state still updates.
    }
  }, []);

  return (
    <ThemeProvider theme={theme} onThemeChange={setTheme}>
      {children}
    </ThemeProvider>
  );
}
